'use client'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { motion } from 'framer-motion'
import { cn } from '@/utils/styles/cn'
import { SuperscaleCloverBadge, SuperscaleMark } from '@/features/landing-page/v3/superscale-mark'
import { PRODUCT_STEPS, type ProductStep } from '@/features/landing-page/v3/sections/product-steps-data'
import { LP_GRID } from '@/features/landing-page/v3/lp-grid'
import { useLandingCarouselAssetsForSlug } from '@/features/landing-page/landing-carousel-assets-context'
import { resolveAssetSlug } from '@/features/landing-page/helpers/landing-route-content'
import type { ILandingCarouselAsset } from '@/features/landing-page/helpers/get-carousel-assets-server'

/**
 * lp-v3 "You decide the direction, we handle the rest" section. One step is active at a time; its
 * description + features expand and the showcase swaps to match.
 *   - Desktop: a two-column layout — heading + vertical accordion on the left, cross-fading showcase
 *     on the right.
 *   - Mobile: heading, a horizontal-scroll strip of step cards (title + description), then the active
 *     step's showcase and features stacked below.
 * Both layouts share `activeId`. Every step's title/description/features ship in the static HTML (the
 * desktop accordion + the mobile cards), so all copy stays crawlable for SEO. `!` beats `.ds2`.
 */
export function LpProductStepsSection() {
    const [activeId, setActiveId] = useState(PRODUCT_STEPS[0].id)
    const active = PRODUCT_STEPS.find((step) => step.id === activeId) ?? PRODUCT_STEPS[0]
    // The active mark only spins after a REAL switch — never on first render, so the SSR HTML ships
    // the resting state (no baked-in rotation). Ref, not state: flipping it must not re-render.
    const hasSwitchedStepRef = useRef(false)
    const mobileScrollerRef = useRef<HTMLDivElement>(null)
    const handleSelectStep = (stepId: string) => {
        hasSwitchedStepRef.current = true
        setActiveId(stepId)
        // Snap the tapped card to the start on mobile so the swipe/click surfaces stay in sync.
        // No-op on desktop (the strip is display:none there, so scrollIntoView doesn't fire).
        const card = mobileScrollerRef.current?.querySelector<HTMLElement>(
            `[data-step-id="${stepId}"]`
        )
        card?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
    }

    // Mobile: swiping/scrolling the card strip drives selection too (not only tapping). The card
    // most visible in the scroller becomes active — matches native carousel feel.
    useEffect(() => {
        const scroller = mobileScrollerRef.current
        if (!scroller) return
        const cards = Array.from(scroller.querySelectorAll<HTMLElement>('[data-step-id]'))
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        const stepId = (entry.target as HTMLElement).dataset.stepId
                        if (stepId) {
                            hasSwitchedStepRef.current = true
                            setActiveId(stepId)
                        }
                    }
                }
            },
            { root: scroller, threshold: 0.7 }
        )
        cards.forEach((card) => observer.observe(card))
        return () => observer.disconnect()
    }, [])

    const heading = (
        <h2 className="text-left">
            <span className="block text-[28px]! font-normal leading-[1.1]! text-foreground md:text-[40px]!">
                One platform to leverage AI,
            </span>
            <span className="mt-1 block font-[family-name:var(--font-landing-pixel)]! text-[28px]! font-bold leading-[1]! tracking-normal! text-[#FF5500]! md:text-[40px]!">
                across your marketing workflow
            </span>
        </h2>
    )

    const featureList = (features: string[]) => (
        <div>
            <span className="text-[10px]! font-bold uppercase leading-[1.04]! tracking-normal! text-[#A8A097]!">
                Features
            </span>
            <div className="mt-2 flex max-w-[320px] flex-col gap-1.5">
                {features.map((feature) => (
                    <span
                        key={feature}
                        className="rounded-[6px] bg-[#F0EEEA] px-3 py-1.5 text-[16px]! leading-[1.25]! tracking-[-0.02em]! text-[#746C64]!"
                    >
                        {feature}
                    </span>
                ))}
            </div>
        </div>
    )

    return (
        <section className={`bg-background ${LP_GRID.contentSection} py-[60px] md:py-[100px]`}>
            <div className="mx-auto max-w-[1320px]">
                {/* MOBILE — heading, horizontal step cards, showcase, features */}
                <div className="md:hidden">
                    {heading}

                    {/* The strip's -mx-4 px-4 cancels the section's px-4 gutter so it spans edge-to-edge,
                        and scroll-pl-4 makes snap-start land the card's left edge at the same 16px inset
                        as the heading text (default scroll-padding would snap it to the viewport edge and
                        clip the left of the card). */}
                    <div
                        ref={mobileScrollerRef}
                        className="-mx-4 mt-8 flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-4 scroll-pl-4 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    >
                        {PRODUCT_STEPS.map((step) => {
                            const isActive = step.id === activeId
                            return (
                                <button
                                    key={step.id}
                                    type="button"
                                    data-step-id={step.id}
                                    onClick={() => handleSelectStep(step.id)}
                                    className="flex w-[76%] shrink-0 snap-start flex-col gap-2 rounded-[14px] bg-[#F0EEEA] p-5 text-left"
                                >
                                    <span className="flex items-center gap-2">
                                        <span
                                            className={cn(
                                                'text-[20px]! leading-[1.1]!',
                                                isActive
                                                    ? 'font-[family-name:var(--font-landing-pixel)]! font-bold tracking-[-0.02em]! text-[#FF5500]!'
                                                    : 'font-normal tracking-normal! text-[#A8A097]!'
                                            )}
                                        >
                                            {step.title}
                                        </span>
                                        {step.comingSoon && (
                                            <span className="shrink-0 rounded-full border border-[#D7D2C9] px-2 py-0.5 text-[9px]! font-bold uppercase leading-[1.04]! tracking-[0.04em] text-[#A8A097]!">
                                                Coming soon
                                            </span>
                                        )}
                                    </span>
                                    <p className="text-[16px]! leading-[1.25]! tracking-[-0.02em]! text-[#746C64]!">
                                        {step.description}
                                    </p>
                                </button>
                            )
                        })}
                    </div>

                    <div className="mt-8">
                        <div className="relative mx-auto aspect-square w-full max-w-[380px]">
                            <StepShowcase key={active.id} step={active} />
                        </div>
                    </div>

                    <div className="mt-8">{featureList(active.features)}</div>
                </div>

                {/* DESKTOP — heading + accordion (left), cross-fading showcase (right) */}
                <div className="hidden grid-cols-2 gap-16 md:grid md:items-center">
                    <div>
                        {heading}

                        <ul className="mt-12 flex flex-col gap-2">
                            {PRODUCT_STEPS.map((step) => {
                                const isActive = step.id === activeId
                                return (
                                    <li key={step.id}>
                                        {/* Titles are NOT indented — the active mark is absolutely positioned in the
                                            left gutter (pl-11), so active + inactive titles share the same left edge. */}
                                        <button
                                            type="button"
                                            onClick={() => handleSelectStep(step.id)}
                                            aria-expanded={isActive}
                                            className="relative flex w-full items-center gap-2 pl-14 text-left"
                                        >
                                            {isActive && (
                                                <motion.span
                                                    layoutId="lp-product-steps-mark"
                                                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                                                    className="absolute left-0 top-1/2 -mt-4"
                                                >
                                                    {/* Spins in half a turn on every switch (only after a
                                                        real interaction — SSR ships the resting state). */}
                                                    <motion.span
                                                        key={step.id}
                                                        initial={hasSwitchedStepRef.current ? { rotate: -180 } : false}
                                                        animate={{ rotate: 0 }}
                                                        transition={{ type: 'spring', stiffness: 180, damping: 22 }}
                                                        className="block"
                                                    >
                                                        {/* 36px ≈ the old mark+halo footprint (the halo used to overflow the box). */}
                                                        <SuperscaleCloverBadge className="size-9" />
                                                    </motion.span>
                                                </motion.span>
                                            )}
                                            <span
                                                className={cn(
                                                    'text-[24px]! leading-[1.1]! transition-colors',
                                                    isActive
                                                        ? 'font-[family-name:var(--font-landing-pixel)]! font-bold leading-[1.15]! tracking-[-0.02em]! text-[#FF5500]!'
                                                        : 'font-normal tracking-normal! text-[#A8A097]! hover:text-foreground!'
                                                )}
                                            >
                                                {step.title}
                                            </span>
                                            {step.comingSoon && (
                                                <span className="shrink-0 rounded-full border border-[#D7D2C9] px-2 py-0.5 text-[9px]! font-bold uppercase leading-[1.04]! tracking-[0.04em] text-[#A8A097]!">
                                                    Coming soon
                                                </span>
                                            )}
                                        </button>

                                        {/* Expanding body — kept in the DOM (SEO); grid-rows animates the height. */}
                                        <div
                                            className={cn(
                                                'grid transition-[grid-template-rows] duration-300 ease-out',
                                                isActive ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                                            )}
                                        >
                                            <div className="overflow-hidden">
                                                <div className="pb-6 pl-14 pt-3">
                                                    <p className="max-w-[400px] text-[16px]! leading-[1.25]! tracking-[-0.02em]! text-[#746C64]!">
                                                        {step.description}
                                                    </p>
                                                    <div className="mt-3">{featureList(step.features)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>

                    {/* Single aspect-square box; only the active showcase renders, keyed so its per-step
                        entrance animation replays on every tab switch. */}
                    <div className="relative aspect-square w-full">
                        <StepShowcase key={active.id} step={active} />
                    </div>
                </div>
            </div>
        </section>
    )
}

const STEP_ASSETS = '/assets/landing-v3/product-steps'

/**
 * Per-step visual. Fills its parent (`h-full`), which owns the square aspect. Context & Research and
 * Analysis both use transparent PNGs (card + prompt-box) whose rotation and drop shadow are baked in —
 * DO NOT add rotate() or the ShowcaseCard shadow wrapper for those. Launch & Scheduling uses a single
 * composite PNG that fills the whole asset column.
 */
function StepShowcase({ step }: { step: ProductStep }) {
    switch (step.id) {
        case 'context-research':
            // report card (back-right) + prompt (front-left), overlapping. Both PNGs ship with their
            // rotation + drop shadow baked in — DO NOT add rotate()/shadow classes here.
            return (
                <div className="relative h-full w-full">
                    <div className="lp-card-in-right absolute right-[2%] top-[6%] w-[54%]">
                        <Image
                            src={`${STEP_ASSETS}/context-card.png`}
                            alt="Superscale competitive intelligence analysis report"
                            width={1073}
                            height={1679}
                            sizes="(max-width: 768px) 54vw, 340px"
                            className="h-auto w-full"
                        />
                    </div>
                    <div
                        className="lp-card-in-left absolute left-[10%] top-[38%] w-[44%]"
                        style={{ animationDelay: '90ms' }}
                    >
                        <Image
                            src={`${STEP_ASSETS}/context-prompt.png`}
                            alt="Superscale prompt: break down AG1's top-performing paid social ads"
                            width={758}
                            height={939}
                            sizes="(max-width: 768px) 44vw, 260px"
                            className="h-auto w-full"
                        />
                    </div>
                </div>
            )
        case 'creative-strategy':
            // Staggered deck: Avolt (back, small, left) → Quinta (middle) → Saye (front, largest, right).
            // Rendered back-to-front so later cards sit on top; each scales down slightly the further back it goes.
            // Cards are STRAIGHT (no rotation) — matches the reference.
            return (
                <div className="relative h-full w-full">
                    <ShowcaseCard src={`${STEP_ASSETS}/creative-avolt.png`} alt="Creative example — Avolt" width={679} height={1211} className="lp-card-in-up absolute left-[0%] top-[22%] w-[34%]" />
                    <ShowcaseCard src={`${STEP_ASSETS}/creative-quinta.png`} alt="Creative example — Quinta Maso" width={801} height={1425} className="lp-card-in-up absolute left-[22%] top-[12%] w-[42%]" style={{ animationDelay: '90ms' }} />
                    <ShowcaseCard src={`${STEP_ASSETS}/creative-saye.png`} alt="Creative example — Saye" width={851} height={1641} className="lp-card-in-up absolute left-[48%] top-[2%] w-[50%]" style={{ animationDelay: '180ms' }} />
                </div>
            )
        case 'statics-video':
            // Same 3-card staggered stack as Creative Strategy, but the assets come from the
            // per-landing-page carousel manifest (same source the magic-stack section uses via
            // resolveAssetSlug(pathname) → home fallback). Handles both video + image assets.
            return <StaticsVideoStack />
        case 'placements':
            // Two columns, 18px gap everywhere. Each card renders at its NATIVE aspect ratio
            // (h-auto, no object-cover crop). Widths are chosen so both columns end up ~the same
            // total height; the right column is padded down so the left card starts a bit higher
            // and the right column ends the same amount lower (symmetric offset).
            //
            // Left  (build-portrait, 714×1272 → 0.561):        width 54%,  height ~96%
            // Right col total (18px gap between two cards):   width 41%,  height ~97%
            //   • top    (build-square,  717×717  → 1.00):    height 41%
            //   • bottom (build-small,   504×630  → 0.80):    height 51%
            return (
                <div className="flex h-full w-full items-start gap-[18px]">
                    <div className="lp-card-in-up w-[54%] shrink-0 overflow-hidden rounded-[14px] shadow-[0_12px_30px_rgba(0,0,0,0.12)]">
                        <Image
                            src={`${STEP_ASSETS}/build-portrait.jpg`}
                            alt="Build for Life — story placement"
                            width={714}
                            height={1272}
                            sizes="(max-width: 768px) 55vw, 340px"
                            className="h-auto w-full"
                        />
                    </div>
                    <div className="flex w-[41%] shrink-0 flex-col gap-[18px] pt-[7%]">
                        <div
                            className="lp-card-in-pop overflow-hidden rounded-[14px] shadow-[0_12px_30px_rgba(0,0,0,0.12)]"
                            style={{ animationDelay: '110ms' }}
                        >
                            <Image
                                src={`${STEP_ASSETS}/build-square.jpg`}
                                alt="Build for Life — feed placement"
                                width={717}
                                height={717}
                                sizes="(max-width: 768px) 42vw, 260px"
                                className="h-auto w-full"
                            />
                        </div>
                        <div
                            className="lp-card-in-pop overflow-hidden rounded-[14px] shadow-[0_12px_30px_rgba(0,0,0,0.12)]"
                            style={{ animationDelay: '210ms' }}
                        >
                            <Image
                                src={`${STEP_ASSETS}/build-small.png`}
                                alt="Build for Life — square placement"
                                width={504}
                                height={630}
                                sizes="(max-width: 768px) 42vw, 260px"
                                className="h-auto w-full"
                            />
                        </div>
                    </div>
                </div>
            )
        case 'launch':
            // Single composite (chair scene + launch prompt overlay + BUILD FOR LIFE headline) — fills
            // the whole asset column with an 8px radius. object-cover so it stretches to both edges;
            // the composition is roughly square (1750×1965) so cropping is minimal.
            return (
                <div className="lp-card-in-pop relative h-full w-full overflow-hidden rounded-[8px]">
                    <Image
                        src={`${STEP_ASSETS}/launch-composite.png`}
                        alt="Launched Build for Life campaign scheduled on Meta"
                        fill
                        sizes="(max-width: 768px) 100vw, 620px"
                        className="object-cover object-center"
                    />
                </div>
            )
        case 'analysis':
            // Same arrangement as context-research: report card (back-right) + prompt (front-left).
            // Rotation + drop shadow are baked into the PNGs — do not add them here.
            return (
                <div className="relative h-full w-full">
                    <div className="lp-card-in-right absolute right-[2%] top-[6%] w-[54%]">
                        <Image
                            src={`${STEP_ASSETS}/analysis-card.png`}
                            alt="Superscale account performance analysis report"
                            width={1073}
                            height={1679}
                            sizes="(max-width: 768px) 54vw, 340px"
                            className="h-auto w-full"
                        />
                    </div>
                    <div
                        className="lp-card-in-left absolute left-[10%] top-[38%] w-[44%]"
                        style={{ animationDelay: '90ms' }}
                    >
                        <Image
                            src={`${STEP_ASSETS}/analysis-prompt.png`}
                            alt="Superscale prompt: break down what has been working in our Meta Ads account over the last 2 weeks"
                            width={758}
                            height={939}
                            sizes="(max-width: 768px) 44vw, 260px"
                            className="h-auto w-full"
                        />
                    </div>
                </div>
            )
        default:
            return (
                <div className="flex h-full w-full items-center justify-center rounded-[16px] bg-[#F0EEEA]">
                    <SuperscaleMark className="size-10 text-[#A8A097]" />
                </div>
            )
    }
}

/**
 * 3 carousel assets (videos or images) staggered like the Creative Strategy stack —
 * back (small, left) → middle → front (large, right). Assets come from the per-landing-page
 * carousel manifest (home fallback), same source as the magic-stack section. Portrait 9:16
 * aspect on every card so a mixed pool of videos + images composes cleanly.
 */
function StaticsVideoStack() {
    const pathname = usePathname()
    const slugAssets = useLandingCarouselAssetsForSlug(resolveAssetSlug(pathname ?? '/'))
    const cards = (slugAssets?.assets ?? []).slice(0, 3)
    if (cards.length < 3) return null

    return (
        <div className="relative h-full w-full">
            <StaticsVideoCard asset={cards[0]} className="lp-card-in-up absolute left-[0%] top-[22%] w-[34%]" />
            <StaticsVideoCard
                asset={cards[1]}
                className="lp-card-in-up absolute left-[22%] top-[12%] w-[42%]"
                style={{ animationDelay: '90ms' }}
            />
            <StaticsVideoCard
                asset={cards[2]}
                className="lp-card-in-up absolute left-[48%] top-[2%] w-[50%]"
                style={{ animationDelay: '180ms' }}
            />
        </div>
    )
}

/** One staggered card in StaticsVideoStack. Renders either a muted, looping <video> or an <Image>. */
function StaticsVideoCard({
    asset,
    className,
    style,
}: {
    asset: ILandingCarouselAsset
    className?: string
    style?: React.CSSProperties
}) {
    return (
        <div
            className={cn(
                'aspect-9/16 overflow-hidden rounded-[14px] shadow-[0_12px_30px_rgba(0,0,0,0.12)]',
                className
            )}
            style={style}
        >
            {asset.type === 'video' ? (
                <video
                    src={asset.src}
                    muted
                    loop
                    autoPlay
                    playsInline
                    preload="metadata"
                    className="size-full object-cover"
                />
            ) : (
                <Image
                    src={asset.src}
                    alt="Ad generated by a Superscale agent"
                    width={540}
                    height={960}
                    sizes="(max-width: 768px) 45vw, 300px"
                    className="size-full object-cover"
                />
            )}
        </div>
    )
}

/** A creative asset (opaque JPG) shown as a rounded, shadowed card. */
function ShowcaseCard({
    src,
    alt,
    width,
    height,
    className,
    style,
}: {
    src: string
    alt: string
    width: number
    height: number
    className?: string
    style?: React.CSSProperties
}) {
    return (
        <div className={cn('overflow-hidden rounded-[14px] shadow-[0_12px_30px_rgba(0,0,0,0.12)]', className)} style={style}>
            <Image
                src={src}
                alt={alt}
                width={width}
                height={height}
                sizes="(max-width: 768px) 45vw, 300px"
                className="h-auto w-full"
            />
        </div>
    )
}
